import { LitElement, css, html } from 'lit'
import gsap from 'gsap'

export default class TypeAnimation extends LitElement {
  static properties = {
    text: { type: String },
    duration: { attribute: 'duration', type: Number },
    easing: { attribute: 'easing', type: String },
    maxSize: { attribute: 'max-size', type: Number }
  };

  constructor() {
    super();
    this.duration = 3;
    this.easing = 'power2.inOut';
    this.maxSize = 1000;
    this.calculatedSize = 80;
    this.variableAxes = {};
  }

  async firstUpdated() {
    this.textElement = this.shadowRoot.querySelector('p');

    // Detect variable axes first
    this.detectVariableAxes();

    // Always calculate optimal font size based on max axis values
    this.calculatedSize = await this.calculateOptimalFontSize();

    // Set initial font size
    gsap.set(this.textElement, { fontSize: `${this.calculatedSize}px` });

    // Start animation
    this.animateVariableFont();
  }

  detectVariableAxes() {
    // Common variable font axes with their typical ranges
    const commonAxes = {
      'wght': { min: 100, max: 900 },    // Weight
      'wdth': { min: 50, max: 200 },     // Width
      'slnt': { min: -15, max: 0 },      // Slant
      'ital': { min: 0, max: 1 },        // Italic
      'opsz': { min: 8, max: 72 }        // Optical Size
    };

    this.variableAxes = {};

    // Test each axis to see if it's supported by this font
    for (const [axis, range] of Object.entries(commonAxes)) {
      if (this.testAxis(axis, range.min, range.max)) {
        this.variableAxes[axis] = range;
        console.log(`Detected variable axis: ${axis} (${range.min}-${range.max})`);
      }
    }

    // Fallback to weight only if no axes detected
    if (Object.keys(this.variableAxes).length === 0) {
      this.variableAxes = { 'wght': { min: 100, max: 900 } };
      console.log('No variable axes detected, falling back to weight only');
    }

    // Set initial values to minimum for all detected axes
    const initialSettings = Object.entries(this.variableAxes)
      .map(([axis, range]) => `"${axis}" ${range.min}`)
      .join(', ');

    gsap.set(this.textElement, {
      fontVariationSettings: initialSettings
    });
  }

  testAxis(axis, min, max) {
    // Test if an axis is supported by temporarily setting it
    try {
      const originalSettings = this.textElement.style.fontVariationSettings;

      // Try setting the axis to its minimum value
      this.textElement.style.fontVariationSettings = `"${axis}" ${min}`;
      const computedMin = getComputedStyle(this.textElement).fontVariationSettings;

      // Try setting the axis to its maximum value
      this.textElement.style.fontVariationSettings = `"${axis}" ${max}`;
      const computedMax = getComputedStyle(this.textElement).fontVariationSettings;

      // Restore original settings
      this.textElement.style.fontVariationSettings = originalSettings;

      // If the computed values include our axis, it's supported
      return computedMin.includes(axis) || computedMax.includes(axis) ||
        computedMin !== computedMax; // Values changed, axis is supported
    } catch (e) {
      console.warn(`Error testing axis ${axis}:`, e);
      return false;
    }
  }

  async calculateOptimalFontSize() {
    // Get container width
    const containerWidth = this.offsetWidth;

    if (!containerWidth || containerWidth === 0) {
      console.log('[TYPE-ANIMATION] No container width, using default size');
      return 80;
    }

    // Build max variation settings
    const maxSettings = Object.entries(this.variableAxes)
      .map(([axis, range]) => `"${axis}" ${range.max}`)
      .join(', ');

    if (maxSettings === '') {
      console.log('[TYPE-ANIMATION] No variable axes detected, calculating without variation settings');
    }

    // Wait for font to load
    const fontFamily = getComputedStyle(this.textElement).fontFamily;
    try {
      await document.fonts.load(`16px ${fontFamily}`);
      console.log('[TYPE-ANIMATION] Font loaded:', fontFamily);
    } catch (error) {
      console.warn('[TYPE-ANIMATION] Font load failed, continuing anyway:', error);
    }

    // Create temp measurement element
    const tempElement = document.createElement('div');
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.whiteSpace = 'nowrap';
    tempElement.style.fontFamily = fontFamily;
    if (maxSettings !== '') {
      tempElement.style.fontVariationSettings = maxSettings;
    }
    tempElement.textContent = this.text;
    document.body.appendChild(tempElement);

    // Find largest size that fits (with 95% safety margin)
    let fontSize = this.maxSize;
    const targetWidth = containerWidth * 0.95;

    while (fontSize > 10) {
      tempElement.style.fontSize = `${fontSize}px`;
      if (tempElement.offsetWidth <= targetWidth) {
        break;
      }
      fontSize -= 1;
    }

    document.body.removeChild(tempElement);

    if (maxSettings !== '') {
      console.log(`[TYPE-ANIMATION] Calculated optimal size: ${fontSize}px for max variation settings: ${maxSettings}`);
    } else {
      console.log(`[TYPE-ANIMATION] Calculated optimal size: ${fontSize}px`);
    }
    return fontSize;
  }

  animateVariableFont() {
    // Create a timeline that loops infinitely with explicit keyframes
    const tl = gsap.timeline({ repeat: -1 });

    // Build font-variation-settings strings for min and max values
    const maxSettings = Object.entries(this.variableAxes)
      .map(([axis, range]) => `"${axis}" ${range.max}`)
      .join(', ');

    const minSettings = Object.entries(this.variableAxes)
      .map(([axis, range]) => `"${axis}" ${range.min}`)
      .join(', ');

    console.log('Animating with settings:', { minSettings, maxSettings });

    // Animate from min to max for all axes
    tl.to(this.textElement, {
      fontVariationSettings: maxSettings,
      duration: this.duration / 2,
      ease: this.easing,
    })
      // Animate back from max to min for all axes
      .to(this.textElement, {
        fontVariationSettings: minSettings,
        duration: this.duration / 2,
        ease: this.easing,
      });

    return tl;
  }

  static styles = css`
    p {
      will-change: font-weight, font-variation-settings;
      transform: translate3d(0, 0, 0);
    }
  `;

  // Render the UI as a function of component state
  render() {
    return html`<p style="margin: 0;">${this.text}</p>`;
  }
}

customElements.define('gt-typeanimation', TypeAnimation)
